import React from 'react';
import { ExternalLink, ShieldCheck, ShieldX, Scale } from 'lucide-react';
import { fmtPx, fmtTime } from './RetestTag';

const sideCls = (side) => (side === 'BULL' ? 'text-buy' : side === 'BEAR' ? 'text-sell' : 'text-[var(--txt-muted)]');

export function SignalBanner({ s }) {
  const sig = s.signal;
  if (sig.actionable) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 border text-[10px] tracking-widest uppercase ${sig.direction === 'LONG' ? 'border-green-500 text-green-300 bg-green-500/10' : 'border-red-500 text-red-300 bg-red-500/10'}`}
           data-testid="retest-signal-banner">
        <ShieldCheck size={13} /> {sig.direction} setup confirmed — momentum aligned, exchanges agree
        <span className="ml-auto normal-case tracking-normal text-[9px] text-[var(--txt-muted)]">{sig.note}</span>
      </div>
    );
  }
  return (
    <div className="px-3 py-2 border border-[var(--border-default)] bg-black/40 text-[10px]" data-testid="retest-signal-banner">
      <div className="flex items-center gap-2 tracking-widest uppercase text-[var(--txt-secondary)]">
        <ShieldX size={13} className="text-amber-300" /> No directional call
      </div>
      {sig.blockers.length > 0
        ? sig.blockers.map((b) => <div key={b} className="text-[9px] text-amber-300/90 mt-0.5" data-testid="retest-signal-blocker">• {b}</div>)
        : <div className="text-[9px] text-[var(--txt-muted)] mt-0.5">Long/Short is only shown once the retest is fully confirmed.</div>}
    </div>
  );
}

function IndRow({ k, c, l, fmt = fmtPx, cls }) {
  return (
    <tr className="border-b border-[var(--border-default)]/50">
      <td className="py-0.5 pr-2 text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">{k}</td>
      <td className={`py-0.5 pr-2 text-right tabular-nums ${cls?.(l) || ''}`}>{l == null ? '—' : fmt(l)}</td>
      <td className={`py-0.5 text-right tabular-nums ${cls?.(c) || ''}`}>{c == null ? '—' : fmt(c)}</td>
    </tr>
  );
}

export function IndicatorsBlock({ ind, tvUrl, venueLabel }) {
  if (!ind) return null;
  const L = ind.live || {}; const C = ind.closed || {};
  const n = (v) => Number(v).toFixed(1);
  const h = (v) => (v > 0 ? 'text-buy' : v < 0 ? 'text-sell' : '');
  return (
    <div className="border border-[var(--border-default)] bg-black/40 p-2.5 min-w-0" data-testid="retest-indicators">
      <div className="flex items-center text-[8px] tracking-[0.35em] uppercase text-cyan-sys mb-2">
        // indicators · {venueLabel}
        {tvUrl && (
          <a href={tvUrl} target="_blank" rel="noreferrer" data-testid="retest-tradingview-link"
             className="ml-auto inline-flex items-center gap-1 tracking-widest text-[var(--txt-secondary)] hover:text-cyan-sys">
            verify on TradingView <ExternalLink size={10} />
          </a>
        )}
      </div>
      <table className="w-full text-[10px]">
        <thead>
          <tr className="text-[8px] tracking-widest uppercase text-[var(--txt-muted)]">
            <th className="text-left font-normal" /><th className="text-right font-normal pr-2">forming bar</th><th className="text-right font-normal">last closed</th>
          </tr>
        </thead>
        <tbody>
          <IndRow k="close" l={L.close} c={C.close} />
          <IndRow k="EMA 20" l={L.ema20} c={C.ema20} />
          <IndRow k="EMA 50" l={L.ema50} c={C.ema50} />
          <IndRow k="EMA 200" l={L.ema200} c={C.ema200} />
          <IndRow k="RSI 14" l={L.rsi14} c={C.rsi14} fmt={n} />
          <IndRow k="MACD" l={L.macd} c={C.macd} />
          <IndRow k="signal" l={L.signal} c={C.signal} />
          <IndRow k="histogram" l={L.hist} c={C.hist} cls={h} />
        </tbody>
      </table>
      <div className="text-[9px] text-[var(--txt-muted)] pt-1">{ind.settings} · {ind.bars} bars · closed {fmtTime(C.time)}</div>
      <div className="text-[9px] text-[var(--txt-muted)]">Confirmation uses closed candles only; the forming bar is shown to match TradingView.</div>
    </div>
  );
}

export function MomentumBlock({ s, own, ltf }) {
  const g = s.momentum_guard;
  const rows = [own, ...(ltf || [])].filter(Boolean);
  return (
    <div className="border border-[var(--border-default)] bg-black/40 p-2.5 min-w-0" data-testid="retest-momentum">
      <div className="text-[8px] tracking-[0.35em] uppercase text-cyan-sys mb-2">// MACD momentum guard</div>
      <div className={`text-[10px] mb-1 ${g.status === 'PASS' ? 'text-buy' : g.status === 'FAIL' ? 'text-sell' : 'text-amber-300'}`} data-testid="retest-momentum-status">
        {g.status === 'PASS' ? 'aligned with this setup' : g.status === 'FAIL' ? 'against this setup — no call' : 'not verified — no call'}
      </div>
      {rows.map((m) => (
        <div key={m.tf} className="flex justify-between text-[10px]" data-testid={`retest-momentum-${m.tf}`}>
          <span className="text-[var(--txt-muted)] uppercase tracking-wider text-[9px]">{m.tf}</span>
          {m.available ? (
            <span className="tabular-nums">
              <span className={sideCls(m.live?.side)}>{m.live ? `${m.live.side} ${fmtPx(m.live.hist)}` : '—'}</span>
              <span className="text-[var(--txt-muted)]"> / </span>
              <span className={sideCls(m.closed.side)}>{m.closed.side} {fmtPx(m.closed.hist)}</span>
            </span>
          ) : <span className="text-zinc-500">no data</span>}
        </div>
      ))}
      <div className="text-[9px] text-[var(--txt-muted)] pt-1">forming / last closed histogram. A long or short is only issued when none of these point the other way.</div>
      {g.conflicts.map((c) => <div key={c} className="text-[9px] text-sell">• {c}</div>)}
    </div>
  );
}

export function CrossVenueBlock({ s }) {
  const cv = s.cross_venue;
  const cls = { AGREE: 'text-buy', NOT_LISTED: 'text-[var(--txt-secondary)]', PARTIAL: 'text-amber-300', DISAGREE: 'text-sell' }[cv.status] || 'text-[var(--txt-muted)]';
  return (
    <div className="border border-[var(--border-default)] bg-black/40 p-2.5 min-w-0" data-testid="retest-cross-venue">
      <div className="text-[8px] tracking-[0.35em] uppercase text-cyan-sys mb-2 flex items-center gap-1"><Scale size={10} /> // cross-exchange check</div>
      <div className={`text-[10px] ${cls}`} data-testid="retest-cross-venue-status">{cv.status.replace(/_/g, ' ')}</div>
      <div className="text-[9px] text-[var(--txt-secondary)]">{cv.reason}</div>
      {cv.price != null && (
        <div className="text-[9px] text-[var(--txt-muted)] tabular-nums">{cv.symbol} ${fmtPx(cv.price)} ({cv.price_diff_pct > 0 ? '+' : ''}{cv.price_diff_pct}% vs this exchange)</div>
      )}
      {cv.status === 'NOT_CHECKED' && <div className="text-[9px] text-[var(--txt-muted)]">only run for confirmed setups</div>}
    </div>
  );
}
